import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { detailedResultAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, CheckCircle, XCircle, MinusCircle, Clock, Trophy,
  BookOpen, Target, BarChart3, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { sanitizeHTML, stripHTML } from '@/lib/sanitize';

export default function DetailedResultPage() {
  const { resultId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  useEffect(() => {
    loadResult();
  }, [resultId]);

  const loadResult = async () => {
    try {
      setLoading(true);
      const res = await detailedResultAPI.get(resultId);
      setResult(res.data);
    } catch (error) {
      console.error('Failed to load result:', error);
      toast.error('Gagal memuat hasil ujian');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (idx) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#4472C4]" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">Hasil tidak ditemukan</p>
          <Button onClick={() => navigate(-1)}>Kembali</Button>
        </div>
      </div>
    );
  }

  const scoreColor = result.percentage >= 70 ? 'text-green-600' : result.percentage >= 50 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="min-h-screen bg-slate-50 py-6">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Hasil Ujian Detail</h1>
            <p className="text-slate-500">{result.tryout?.title}</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 text-center">
            <Trophy className={`w-8 h-8 mx-auto mb-2 ${scoreColor}`} />
            <p className={`text-3xl font-bold ${scoreColor}`}>{result.percentage?.toFixed(1)}%</p>
            <p className="text-sm text-slate-500">Nilai Akhir</p>
          </Card>
          
          <Card className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-3xl font-bold text-green-600">{result.correct_count}</p>
            <p className="text-sm text-slate-500">Benar</p>
          </Card>
          
          <Card className="p-4 text-center">
            <XCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-3xl font-bold text-red-600">{result.wrong_count}</p>
            <p className="text-sm text-slate-500">Salah</p>
          </Card>
          
          <Card className="p-4 text-center">
            <MinusCircle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-3xl font-bold text-slate-600">{result.blank_count}</p>
            <p className="text-sm text-slate-500">Kosong</p>
          </Card>
        </div>

        {/* Analysis Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* By Mapel */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#4472C4]" />
              Analisis per Mata Pelajaran
            </h3>
            <div className="space-y-4">
              {Object.entries(result.by_mapel || {}).map(([mapel, stats]) => {
                const percentage = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
                return (
                  <div key={mapel}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">{mapel}</span>
                      <span className="text-sm text-slate-500">
                        {stats.correct}/{stats.total} ({percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="flex gap-1 h-3">
                      <div 
                        className="bg-green-500 rounded-l transition-all"
                        style={{ width: `${(stats.correct / stats.total) * 100}%` }}
                      />
                      <div 
                        className="bg-red-500 transition-all"
                        style={{ width: `${(stats.wrong / stats.total) * 100}%` }}
                      />
                      <div 
                        className="bg-slate-300 rounded-r transition-all"
                        style={{ width: `${(stats.blank / stats.total) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* By Difficulty */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#4472C4]" />
              Analisis per Tingkat Kesulitan
            </h3>
            <div className="space-y-4">
              {Object.entries(result.by_difficulty || {}).map(([difficulty, stats]) => {
                const labels = { easy: 'Mudah', medium: 'Sedang', hard: 'Sulit' };
                const colors = { easy: 'bg-green-500', medium: 'bg-yellow-500', hard: 'bg-red-500' };
                const percentage = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
                
                return (
                  <div key={difficulty}>
                    <div className="flex justify-between items-center mb-1">
                      <Badge variant="secondary">{labels[difficulty] || difficulty}</Badge>
                      <span className="text-sm text-slate-500">
                        {stats.correct}/{stats.total} ({percentage.toFixed(0)}%)
                      </span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Question Breakdown */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#4472C4]" />
            Pembahasan Soal ({result.breakdown?.length || 0} soal)
          </h3>
          
          <div className="space-y-3">
            {(result.breakdown || []).map((q, idx) => (
              <div 
                key={idx} 
                className={`border rounded-lg overflow-hidden ${
                  q.is_correct ? 'border-green-200 bg-green-50/50' : 
                  q.is_blank ? 'border-slate-200 bg-slate-50/50' : 'border-red-200 bg-red-50/50'
                }`}
              >
                {/* Question Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => toggleQuestion(idx)}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-white border flex items-center justify-center text-sm font-medium">
                      {idx + 1}
                    </span>
                    {q.is_correct ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : q.is_blank ? (
                      <MinusCircle className="w-5 h-5 text-slate-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900 line-clamp-1">
                        {stripHTML(q.question_text)}...
                      </p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{q.mapel}</Badge>
                        {q.kategori && <Badge variant="outline" className="text-xs">{q.kategori}</Badge>}
                      </div>
                    </div>
                  </div>
                  {expandedQuestions.has(idx) ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>

                {/* Expanded Content */}
                {expandedQuestions.has(idx) && (
                  <div className="border-t p-4 bg-white">
                    {/* Question */}
                    <div className="mb-4">
                      <p className="text-sm font-medium text-slate-500 mb-1">Soal:</p>
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(q.question_text) }}
                      />
                    </div>
                    
                    {/* Answers */}
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-1">Jawaban Anda:</p>
                        <div className={`p-3 rounded-lg ${
                          q.is_correct ? 'bg-green-100 text-green-800' : 
                          q.is_blank ? 'bg-slate-100 text-slate-600' : 'bg-red-100 text-red-800'
                        }`}>
                          {q.is_blank ? '(Tidak dijawab)' : q.user_answer}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-1">Jawaban Benar:</p>
                        <div className="p-3 rounded-lg bg-green-100 text-green-800">
                          {q.correct_answer}
                        </div>
                      </div>
                    </div>
                    
                    {/* Explanation */}
                    {q.explanation && (
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-1">Pembahasan:</p>
                        <div 
                          className="p-3 bg-blue-50 rounded-lg prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: sanitizeHTML(q.explanation) }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Time Info */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <Clock className="w-4 h-4 inline mr-1" />
          Waktu pengerjaan: {formatTime(result.time_spent_seconds || 0)} | 
          Dikumpulkan: {new Date(result.submitted_at).toLocaleString('id-ID')}
        </div>
      </div>
    </div>
  );
}
